import { memo, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { callCheckOnetimePasswordApi, CheckOnetimePasswordResData } from 'requests/RequestUser';
import { RoutePath } from 'consts/RoutePath';
import { CircularProgress } from '@material-ui/core';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';
import { ApiError } from 'requests/RequestBase';
import { ApiErrorCode, findErrorMessageByApiError } from 'consts/ApiErrorCode';
import classes from './override.module.scss';
import { useStore } from 'store/StoreContext';
import { convertObjectToGetParameterWithEncode } from 'common/ObjectToGetParameterConverter';

type Props = {};

export const ForgotIdConfirmTemplate = memo((_props: Props) => {
	const history = useHistory<{ mailaddress: string }>();
	const [store , dispatch] = useStore(); // eslint-disable-line
	const mailaddress = history.location.state.mailaddress;
	const [formData, setFormData] = useState({ onetime_password: '' });
	const [formErrors, setFormErrors] = useState({ onetime_password: '' });
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [processLoading, setProcessLoading] = useState(false);
	const DEFAULT_PAGE = 0;
	const COMPLETE_PAGE = 1;
	const [page, setPage] = useState(DEFAULT_PAGE);
	const [id, setId] = useState('');

	// ワンタイムパスワード入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, onetime_password: event.target.value }));
	};

	// 確認ボタンのonClickで呼ぶ
	const checkOnetimePassword = async () => {
		setProcessLoading(true);

		// エラー解除
		setFormErrors((prev) => ({ ...prev, onetime_password: '' }));

		// ワンタイムパスワード検証
		if (!formData.onetime_password.trim()) {
			setFormErrors((prev) => ({ ...prev, onetime_password: ReactError.E0001 }));
			setProcessLoading(false);
			return;
		}
		const checkOnetimePasswordResult = await callCheckOnetimePasswordApi({
			mailaddress,
			onetimepass: formData.onetime_password,
		});
		if (checkOnetimePasswordResult.status !== HttpStatus.OK) {
			const error = checkOnetimePasswordResult.data as ApiError;
			if (error.error_code === ApiErrorCode.E1157.code) {
				setFormErrors((prev) => ({ ...prev, onetime_password: findErrorMessageByApiError(error) }));
				setProcessLoading(false);
				return;
			}
			history.push(RoutePath.ERROR);
			return;
		}
		const data = checkOnetimePasswordResult.data as CheckOnetimePasswordResData;
		if (!data.exist) {
			setFormErrors((prev) => ({ ...prev, onetime_password: 'ワンタイムパスワードが正しくありません。' }));
			setProcessLoading(false);
			return;
		}
		setId(data.gid);
		setPage(COMPLETE_PAGE);
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenID忘れ｜認証コード入力';
	}, [page]);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors]);

	const [returnTransitioning, setReturnTransitioning] = useState(false);
	const returnTop = () => {
		setReturnTransitioning(true);
		if (!!store.redirectLoginData.redirect_uri) {
			const getParameters = convertObjectToGetParameterWithEncode(store.redirectLoginData, 'redirect_uri', 'client_id');
			history.push(`${RoutePath.REDIRECT_LOGIN}?${getParameters}`);
		} else {
			history.push(RoutePath.LOGIN);
		}
	};

	return (
		<>
			<section className="section">
				<div className="section-in" ref={scrollRef}>
					<div className="section-head">GakkenID忘れ</div>
					{page === DEFAULT_PAGE && (
						<>
							<div className="section-txt-box">
								<div className="txt">
									<div>ご登録いただいたメールアドレスへメールをお送りしました。</div>
									<div className={classes.onetimePassSubTitle}>件名：【GakkenID】GakkenID忘れのご案内</div>
									<div>2時間以内にメールに記載されたワンタイムパスワードを入力し、「確認」ボタンを押してください。</div>
									<div>ページ遷移後、ご自身のGakkenIDが表示されます。</div>
								</div>
							</div>

							{isFormError ? (
								<div className="error-box" ref={errorRef}>
									<>
										<p>※下記項目は全て必須となります。</p>
										<div className="error-box-in">
											{formErrors.onetime_password !== '' ? (
												<p>【ワンタイムパスワード】{formErrors.onetime_password}</p>
											) : null}
										</div>
									</>
								</div>
							) : null}

							<div className="form-box">
								<div className="form-head">
									<label htmlFor="">ワンタイムパスワード</label>
								</div>
								<div className="form-input">
									<input
										type="password"
										name="onetime_password"
										value={formData.onetime_password}
										onChange={handleChangeValue}
									/>
								</div>
								<p className="error">{formErrors.onetime_password}</p>
							</div>
							<div className="btn-box">
								<div className="btn gr">
									{processLoading ? <CircularProgress /> : <button onClick={checkOnetimePassword}>確認</button>}
								</div>
							</div>
						</>
					)}
					{page === COMPLETE_PAGE && (
						<>
							<div className="section-txt-box">
								<p>あなたのGakkenIDは下記のとおりです。</p>
							</div>
							<div className={classes.greenBack}>
								<p className={classes.greenBackIDTitle}>{id}</p>
							</div>
							<div className="btn-box">
								<div className="btn gr">
									<button onClick={returnTop}>ログイン画面へ戻る</button>
								</div>
							</div>
						</>
					)}
				</div>
			</section>
		</>
	);
});
